import { clsx } from 'clsx'
import {
  CalendarDays,
  ChevronDown,
  Circle,
  Eraser,
  Hand,
  Highlighter,
  ImagePlus,
  Minus,
  MousePointer2,
  MoveUpRight,
  Paintbrush,
  PenLine,
  Signature,
  Square,
  Stamp,
  StickyNote,
  Strikethrough,
  TextCursorInput,
  Type,
  Underline,
} from 'lucide-react'
import { type ComponentType, useState } from 'react'
import { pickAndPlaceImage, placeDateText, placeStamp, renderStamp, STAMPS } from '@/lib/images'
import { useUi } from '@/store/uiStore'
import type { Tool } from '@/types'
import { Divider } from '../ui/Button'
import { MenuItem, MenuLabel, Popover } from '../ui/Popover'

type Icon = ComponentType<{ className?: string; strokeWidth?: number }>

export const TOOL_META: Record<Tool, { label: string; icon: Icon; key?: string }> = {
  select: { label: 'Select', icon: MousePointer2, key: 'V' },
  hand: { label: 'Pan', icon: Hand, key: 'H' },
  editText: { label: 'Edit text', icon: TextCursorInput, key: 'E' },
  text: { label: 'Add text', icon: Type, key: 'T' },
  highlight: { label: 'Highlight', icon: Highlighter, key: 'U' },
  underline: { label: 'Underline', icon: Underline },
  strikeout: { label: 'Strikethrough', icon: Strikethrough },
  pen: { label: 'Pen', icon: PenLine, key: 'P' },
  marker: { label: 'Highlighter pen', icon: Paintbrush, key: 'M' },
  rect: { label: 'Rectangle', icon: Square, key: 'R' },
  ellipse: { label: 'Ellipse', icon: Circle, key: 'O' },
  line: { label: 'Line', icon: Minus, key: 'L' },
  arrow: { label: 'Arrow', icon: MoveUpRight, key: 'A' },
  note: { label: 'Note', icon: StickyNote, key: 'N' },
  signature: { label: 'Sign', icon: Signature, key: 'S' },
  image: { label: 'Image', icon: ImagePlus, key: 'I' },
  stamp: { label: 'Stamp', icon: Stamp },
  eraser: { label: 'Eraser', icon: Eraser, key: 'X' },
}

const GROUPS: { id: string; tools: Tool[] }[] = [
  { id: 'markup', tools: ['highlight', 'underline', 'strikeout'] },
  { id: 'draw', tools: ['pen', 'marker'] },
  { id: 'shapes', tools: ['rect', 'ellipse', 'line', 'arrow'] },
]

function ToolButton({ tool, compact }: { tool: Tool; compact?: boolean }) {
  const active = useUi((s) => s.tool === tool)
  const meta = TOOL_META[tool]
  return (
    <button
      type="button"
      aria-label={meta.label}
      aria-pressed={active}
      data-tip={meta.key ? `${meta.label}  ${meta.key}` : meta.label}
      onClick={() => useUi.getState().setTool(active && tool !== 'select' ? 'select' : tool)}
      className={clsx(
        'tip relative flex shrink-0 select-none items-center justify-center rounded-xl transition-[background,color,transform] active:scale-95',
        compact ? 'h-11 w-11 flex-col gap-0.5' : 'size-9',
        active ? 'bg-accent text-on-accent shadow-sm' : 'text-fg-2 hover:bg-surface-3 hover:text-fg',
      )}
    >
      <meta.icon className="size-[19px]" strokeWidth={1.9} />
    </button>
  )
}

function GroupButton({ tools, compact }: { tools: Tool[]; compact?: boolean }) {
  const current = useUi((s) => s.tool)
  const [last, setLast] = useState<Tool>(tools[0])
  const shown = tools.includes(current) ? current : last
  const active = tools.includes(current)
  const meta = TOOL_META[shown]
  return (
    <div
      className={clsx(
        'flex shrink-0 items-center rounded-xl transition-colors',
        active ? 'bg-accent text-on-accent shadow-sm' : 'text-fg-2',
      )}
    >
      <button
        type="button"
        aria-label={meta.label}
        aria-pressed={active}
        data-tip={meta.key ? `${meta.label}  ${meta.key}` : meta.label}
        onClick={() => useUi.getState().setTool(active ? 'select' : shown)}
        className={clsx(
          'tip flex items-center justify-center rounded-l-xl transition-[background,transform] active:scale-95',
          compact ? 'h-11 w-9' : 'h-9 w-8 pl-0.5',
          !active && 'hover:bg-surface-3 hover:text-fg',
        )}
      >
        <meta.icon className="size-[19px]" strokeWidth={1.9} />
      </button>
      <Popover
        align="center"
        side={compact ? 'top' : 'bottom'}
        trigger={({ toggle, ref }) => (
          <button
            ref={ref}
            type="button"
            aria-label={`More ${meta.label.toLowerCase()} tools`}
            onClick={toggle}
            className={clsx(
              'flex items-center justify-center rounded-r-xl transition-colors',
              compact ? 'h-11 w-5' : 'h-9 w-4',
              !active && 'hover:bg-surface-3 hover:text-fg',
            )}
          >
            <ChevronDown className="size-3" strokeWidth={2.5} />
          </button>
        )}
      >
        {(close) => (
          <div className="w-48">
            {tools.map((t) => {
              const M = TOOL_META[t]
              return (
                <MenuItem
                  key={t}
                  icon={<M.icon />}
                  label={M.label}
                  hint={M.key}
                  onSelect={() => {
                    setLast(t)
                    useUi.getState().setTool(t)
                    close()
                  }}
                />
              )
            })}
          </div>
        )}
      </Popover>
    </div>
  )
}

function InsertMenu({ compact }: { compact?: boolean }) {
  return (
    <Popover
      align="center"
      side={compact ? 'top' : 'bottom'}
      trigger={({ toggle, ref, open }) => (
        <button
          ref={ref}
          type="button"
          aria-label="Stamps"
          data-tip="Stamps & checkmarks"
          onClick={toggle}
          className={clsx(
            'tip flex shrink-0 items-center justify-center rounded-xl text-fg-2 transition-colors hover:bg-surface-3 hover:text-fg',
            compact ? 'h-11 w-11' : 'size-9',
            open && 'bg-surface-3 text-fg',
          )}
        >
          <Stamp className="size-[19px]" strokeWidth={1.9} />
        </button>
      )}
    >
      {(close) => (
        <div className="w-60">
          <MenuLabel>Fill & sign</MenuLabel>
          <div className="grid grid-cols-4 gap-1 px-1.5 pb-1">
            {STAMPS.slice(0, 3).map((s) => (
              <button
                key={s.id}
                type="button"
                aria-label={s.label}
                title={s.label}
                onClick={() => {
                  placeStamp(s.id)
                  close()
                }}
                className="flex h-10 items-center justify-center rounded-xl border border-line hover:bg-surface-3"
              >
                <img src={renderStamp(s.id).img.src} alt="" className="size-6" />
              </button>
            ))}
            <button
              type="button"
              aria-label="Today's date"
              title="Today's date"
              onClick={() => {
                placeDateText()
                close()
              }}
              className="flex h-10 items-center justify-center rounded-xl border border-line hover:bg-surface-3"
            >
              <CalendarDays className="size-5 text-fg-2" />
            </button>
          </div>
          <MenuLabel>Stamps</MenuLabel>
          {STAMPS.slice(3).map((s) => (
            <MenuItem
              key={s.id}
              icon={<span className="size-3 rounded-sm border-2" style={{ borderColor: s.color }} />}
              label={s.label}
              onSelect={() => {
                placeStamp(s.id)
                close()
              }}
            />
          ))}
        </div>
      )}
    </Popover>
  )
}

/** The main tool palette. Floating pill on desktop, scrollable dock on phones. */
export function Toolbar({ compact = false }: { compact?: boolean }) {
  const action = (label: string, Icon: Icon, onClick: () => void, tip?: string) => (
    <button
      type="button"
      aria-label={label}
      data-tip={tip ?? label}
      onClick={onClick}
      className={clsx(
        'tip flex shrink-0 items-center justify-center rounded-xl text-fg-2 transition-[background,color,transform] hover:bg-surface-3 hover:text-fg active:scale-95',
        compact ? 'h-11 w-11' : 'size-9',
      )}
    >
      <Icon className="size-[19px]" strokeWidth={1.9} />
    </button>
  )
  return (
    <div
      role="toolbar"
      aria-label="Tools"
      className={clsx(
        'no-scrollbar flex items-center gap-0.5',
        compact
          ? 'overflow-x-auto px-2 py-1'
          : 'rounded-2xl border border-line bg-glass p-1 shadow-md backdrop-blur-xl',
      )}
    >
      <ToolButton tool="select" compact={compact} />
      <ToolButton tool="hand" compact={compact} />
      <Divider />
      <ToolButton tool="editText" compact={compact} />
      <ToolButton tool="text" compact={compact} />
      <Divider />
      <GroupButton tools={GROUPS[0].tools} compact={compact} />
      <GroupButton tools={GROUPS[1].tools} compact={compact} />
      <GroupButton tools={GROUPS[2].tools} compact={compact} />
      <ToolButton tool="eraser" compact={compact} />
      <Divider />
      <ToolButton tool="note" compact={compact} />
      {action('Sign', Signature, () => useUi.getState().openDialog('signature'), 'Sign  S')}
      {action('Image', ImagePlus, () => void pickAndPlaceImage(), 'Insert image  I')}
      <InsertMenu compact={compact} />
    </div>
  )
}
